import { ApiError, toApiError } from './problem'

/**
 * The HTTP seam. Every call to the API goes through `request`.
 *
 * Auth is injected rather than imported, so this module has no dependency on the
 * token store and stays trivially testable. Stage 3 calls `configureApiClient`
 * once at start-up to supply the real token getter and the refresh hook.
 */
export interface ApiClientConfig {
  /**
   * Prefix for every path. Defaults to `/api/v1`, which in dev goes through the
   * Vite proxy to the API — same origin, so no CORS.
   */
  baseUrl: string
  getAccessToken?: (() => string | null) | undefined
  /**
   * Invoked once when a request returns 401. Resolve `true` if a fresh access
   * token is now available and the request should be retried exactly once.
   *
   * Single-flighting refresh is the hook's job, not this module's: several
   * concurrent 401s must share one refresh, and only Stage 3 knows how.
   */
  onUnauthorized?: (() => Promise<boolean>) | undefined
}

const DEFAULT_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

let config: ApiClientConfig = { baseUrl: DEFAULT_BASE_URL }

export function configureApiClient(next: Partial<ApiClientConfig>): void {
  config = { ...config, ...next }
}

/** Test seam — restores the module to its start-up state. */
export function resetApiClient(): void {
  config = { baseUrl: DEFAULT_BASE_URL }
}

export function apiBaseUrl(): string {
  return config.baseUrl
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Serialized as JSON unless it is FormData, which is passed through. */
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null> | undefined
  signal?: AbortSignal | undefined
  headers?: Record<string, string>
  /** Skips the bearer header. Used by login and refresh. */
  anonymous?: boolean
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`
  if (query === undefined) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value))
  }
  const serialized = params.toString()
  return serialized.length > 0 ? `${url}?${serialized}` : url
}

function buildInit(options: RequestOptions): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  // FormData sets its own multipart boundary; setting Content-Type here breaks it.
  if (options.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  if (options.anonymous !== true) {
    const token = config.getAccessToken?.()
    if (token !== null && token !== undefined && token.length > 0) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  }

  if (options.signal !== undefined) init.signal = options.signal
  if (options.body !== undefined) {
    init.body = isFormData ? (options.body as FormData) : JSON.stringify(options.body)
  }

  return init
}

async function parse<T>(response: Response): Promise<T> {
  // 204, and 200s with an empty body, are legitimate for DELETE and some PUTs.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) {
    return (await response.text()) as T
  }

  const text = await response.text()
  return text.length === 0 ? (undefined as T) : (JSON.parse(text) as T)
}

/**
 * Issues one request, retrying exactly once after a successful token refresh.
 *
 * Throws {@link ApiError} for every non-2xx response and for network failures, so
 * callers — and TanStack Query — deal with one error type.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query)

  const send = async (): Promise<Response> => {
    try {
      return await fetch(url, buildInit(options))
    } catch (cause) {
      // fetch rejects only on network-level failure; a 500 is a resolved promise.
      throw new ApiError({
        kind: 'network',
        status: 0,
        message: cause instanceof Error ? cause.message : 'Network request failed.',
      })
    }
  }

  let response = await send()

  if (
    response.status === 401 &&
    options.anonymous !== true &&
    config.onUnauthorized !== undefined
  ) {
    const refreshed = await config.onUnauthorized()
    if (refreshed) {
      response = await send()
    }
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  return parse<T>(response)
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
